import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import ContactValidation from 'donor-elf-web/validations/contact';
import ContactModel, { generateNameFields } from 'donor-elf-web/models/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';

interface ComponentArgs {
  contact: ContactModel;
}

export default class ContactDetailsNamesComponent extends Component<ComponentArgs> {
  @service declare abilities: AbilitiesService;

  @tracked showMorePresentNameFields = false;
  @tracked editing = false;
  @tracked individualNameFieldChanged = false;
  @tracked changeset?: any;
  @tracked showValidationErrors = false;

  get showEditIcon() {
    return (
      this.abilities.hasAbility('edit_contact') &&
      !this.args.contact.anonymousDonor &&
      !this.editing
    );
  }

  get hasMoreNameFieldsPresent() {
    return (
      isPresent(this.args.contact.mailingName) ||
      isPresent(this.args.contact.greeting) ||
      isPresent(this.args.contact.firstName) ||
      isPresent(this.args.contact.lastName) ||
      isPresent(this.args.contact.spouseFirstName) ||
      isPresent(this.args.contact.spouseLastName)
    );
  }

  @action
  handleContactChanged() {
    this.showMorePresentNameFields = false;
    this.editing = false;
    this.individualNameFieldChanged = false;
    this.changeset = undefined;
    this.showValidationErrors = false;
  }

  @action
  setEditing(val: boolean) {
    this.editing = val;
    if (this.editing) {
      this.showMorePresentNameFields = true;
      this.individualNameFieldChanged = false;
      this.showValidationErrors = false;
      this.changeset = new Changeset(
        this.args.contact,
        lookupValidator(ContactValidation),
        ContactValidation,
      );
    }
  }

  @action
  setShowMorePresentNameFields(val: boolean) {
    this.showMorePresentNameFields = val;
  }

  @action
  handleIndividualNameFieldChanged() {
    this.individualNameFieldChanged = true;
  }

  @action
  updateGeneratedNames() {
    let changeset = this.changeset;
    let names = generateNameFields({
      firstName: changeset.get('firstName'),
      lastName: changeset.get('lastName'),
      spouseFirstName: changeset.get('spouseFirstName'),
      spouseLastName: changeset.get('spouseLastName'),
    });
    changeset.set('name', names.name);
    changeset.set('mailingName', names.mailingName);
    changeset.set('greeting', names.greeting);
    this.individualNameFieldChanged = false;
  }

  @action
  async save() {
    // trim all the strings
    this.changeset.changes.forEach((change: any) => {
      if (isPresent(change.value) && typeof change.value === 'string') {
        this.changeset.set(change.key, change.value.trim());
      }
    });

    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.showMorePresentNameFields = true;
      this.showValidationErrors = false;
      this.editing = false;
      await this.changeset.save();
    } else {
      this.showValidationErrors = true;
    }
  }
}
